import { Currency } from "@redotech/money/currencies";
import { useLoad } from "@redotech/react-util/load";
import { CheckoutWidget } from "@redotech/redo-conversion-core-common/checkouts/checkout-widget";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoConversionRpcClientContext } from "@redotech/redo-merchant-app-common/redo-conversion-rpc-client-provider";
import { checkoutWidgetToBuilderState } from "@redotech/redo-web-conversion/checkouts/checkout-widgets-builder/checkout-widget-to-builder-state";
import { CheckoutWidgetsBuilderContext } from "@redotech/redo-web-conversion/checkouts/checkout-widgets-builder/checkout-widgets-builder-context";
import { WidgetPreview } from "@redotech/redo-web-conversion/checkouts/checkout-widgets-builder/preview-panel";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useMemo } from "react";
import * as css from "./checkout-experience-node.module.css";
import { selectWidgetsForExperiencePreview } from "./select-widgets-for-experience-preview";

/** Shows the widget previews configured for a checkout experience. */
export const CheckoutExperienceWidgetsHover = memo(
  function CheckoutExperienceWidgetsHover({
    checkoutExperienceId,
  }: {
    checkoutExperienceId: string | undefined;
  }) {
    const { t } = useTranslation();
    const conversionRpcClient = useContext(RedoConversionRpcClientContext);

    const widgetsLoad = useLoad(
      async (signal) => {
        if (!conversionRpcClient || !checkoutExperienceId) {
          return undefined;
        }
        const allWidgets = await conversionRpcClient.getCheckoutWidgets(
          {},
          { signal },
        );
        return selectWidgetsForExperiencePreview(
          allWidgets,
          checkoutExperienceId,
        );
      },
      [conversionRpcClient, checkoutExperienceId],
    );

    const widgets = widgetsLoad.value ?? [];

    if (widgetsLoad.pending && widgetsLoad.value === undefined) {
      return (
        <Text fontSize="xs" textColor="secondary">
          {t("Loading previews…")}
        </Text>
      );
    }

    if (widgets.length === 0) {
      return (
        <Text fontSize="xs" textColor="secondary">
          {t("No widget preview available.")}
        </Text>
      );
    }

    return (
      <Flex className={css.widgetHoverPreviewList} dir="column" gap="xl">
        {widgets.map((widget) => (
          <Flex dir="column" gap="xs" key={widget._id}>
            <Text fontSize="xs" fontWeight="semibold" textColor="primary">
              {widget.name}
            </Text>
            <SingleWidgetPreview widget={widget} />
          </Flex>
        ))}
      </Flex>
    );
  },
);

const SingleWidgetPreview = memo(function SingleWidgetPreview({
  widget,
}: {
  widget: CheckoutWidget;
}) {
  const contextValue = useMemo(
    () => ({
      selectedWidget: widget.type,
      setSelectedWidget: () => {},
      handleSaveWidgets: () => Promise.resolve(),
      onComplete: () => {},
      // Convert the persisted widget shape for the builder preview.
      checkoutWidgets: checkoutWidgetToBuilderState(widget, Currency.USD),
      setCheckoutWidgets: () => {},
      teamId: "",
      defaultCurrency: Currency.USD,
      searchCollections: async () => [],
      searchProducts: async () => undefined,
      // Report integrations as connected for the preview.
      frameworkIntegrationApi: {
        openConnectModal: () => {},
        disconnect: () => Promise.resolve(),
        getStatus: () => Promise.resolve({ connected: true }),
      },
    }),
    [widget],
  );

  return (
    <div className={css.widgetPreviewScale}>
      <CheckoutWidgetsBuilderContext.Provider value={contextValue}>
        <WidgetPreview type={widget.type} />
      </CheckoutWidgetsBuilderContext.Provider>
    </div>
  );
});
